import { useRef, useState } from 'react';
import type { EnvironmentConfigDTO, PrivateFileDTO } from '@devrobot/shared';
import { Download, FileLock2, Trash2, Upload } from 'lucide-react';
import { api, seg } from '../../../api/client';
import { qk, useApiMutation } from '../../../api/queries';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { EmptyState } from '../../../components/EmptyState';
import { ErrorAlert } from '../../../components/ErrorAlert';
import { RelativeTime } from '../../../components/RelativeTime';
import { StatusBadge } from '../../../components/StatusBadge';
import { useToast } from '../../../components/Toaster';
import { Haptic } from '../../../kit/haptics';
import { haptic } from '../../../kit/native';
import { formatBytes } from '../../../lib/format';

const MAX_UPLOAD = 3 * 1024 * 1024;

export function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Private configuration files declared by the manifest (content never displayed inline). */
export function PrivateFiles({ projectKey, cfg }: { projectKey: string; cfg: EnvironmentConfigDTO }) {
  const toast = useToast();
  const env = cfg.environment;
  const base = `/api/v1/projects/${seg(projectKey)}/environments/${seg(env)}/files`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadFor, setUploadFor] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<PrivateFileDTO | null>(null);
  const [downloading, setDownloading] = useState<PrivateFileDTO | null>(null);
  const invalidate = [qk.config(projectKey, env), qk.environments(projectKey), qk.targets(projectKey)];

  const upload = useApiMutation((v: { name: string; contentBase64: string }) => api.put<PrivateFileDTO>(`${base}/${seg(v.name)}`, { contentBase64: v.contentBase64 }), { invalidate });
  const del = useApiMutation((name: string) => api.delete<{ ok: true }>(`${base}/${seg(name)}`), { invalidate });
  const reveal = useApiMutation((name: string) => api.post<{ contentBase64: string }>(`${base}/${seg(name)}/reveal`));

  const pick = (name: string) => {
    setUploadFor(name);
    setUploadError(null);
    inputRef.current?.click();
  };

  const onFile = async (file: File | undefined) => {
    const name = uploadFor;
    if (inputRef.current) inputRef.current.value = '';
    if (!file || !name) return;
    if (file.size > MAX_UPLOAD) {
      haptic('error');
      setUploadError(`${file.name} is ${formatBytes(file.size)}; files over ${formatBytes(MAX_UPLOAD)} cannot be uploaded from the browser. Use the CLI instead.`);
      return;
    }
    upload.mutate(
      { name, contentBase64: toBase64(await file.arrayBuffer()) },
      {
        onSuccess: () => toast.success(`${name} uploaded`, { description: 'Targets receive it on the next deployment or configuration apply.' }),
        onError: () => haptic('error'),
      },
    );
  };

  const save = (name: string, b64: string) => {
    const url = URL.createObjectURL(new Blob([fromBase64(b64)], { type: 'application/octet-stream' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (cfg.files.length === 0) {
    return (
      <EmptyState icon={FileLock2} title="No private files">
        Declare private files (for example a credentials JSON) under <code className="font-mono">config.files</code> in devrobot.yaml; their content is then provided here per environment and never stored in the repository.
      </EmptyState>
    );
  }

  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" className="hidden" onChange={(e) => void onFile(e.target.files?.[0])} aria-hidden="true" tabIndex={-1} />
      {uploadError ? (
        <div role="alert" className="alert alert-error alert-soft animate-rise text-sm">
          {uploadError}
        </div>
      ) : null}
      <ErrorAlert error={upload.error} title="The file was not uploaded" />
      <div className="-mx-4 overflow-x-auto border-y border-hairline sm:mx-0 sm:rounded-field sm:border">
        <table className="table table-sm">
          <thead>
            <tr className="text-xs tracking-wide text-base-content/50 uppercase">
              <th>File</th>
              <th className="max-md:hidden">Delivered to</th>
              <th className="max-lg:hidden">Mode / owner</th>
              <th>State</th>
              <th className="max-sm:hidden">Current version</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {cfg.files.map((f) => (
              <tr key={f.name} className="hover:bg-base-200/50">
                <td>
                  <span className="font-mono text-sm">{f.name}</span>
                  <div className="flex gap-1">
                    {f.required ? <span className="badge badge-ghost badge-xs">required</span> : null}
                    {!f.declared ? <span className="badge badge-ghost badge-xs">not in manifest</span> : null}
                  </div>
                  {/* Details of the columns hidden on narrow screens. */}
                  {f.destination ? <div className="break-anywhere font-mono text-xs text-base-content/55 md:hidden">{f.destination}</div> : null}
                  {f.state === 'set' ? (
                    <div className="text-xs text-base-content/55 sm:hidden">
                      v{f.version} · {formatBytes(f.size)}
                    </div>
                  ) : null}
                </td>
                <td className="text-xs max-md:hidden">
                  <span className="break-anywhere font-mono">{f.destination ?? '—'}</span>
                  {f.services.length ? <div className="text-base-content/60">{f.services.join(', ')}</div> : null}
                </td>
                <td className="font-mono text-xs max-lg:hidden">
                  {f.mode ?? '—'}
                  {f.uid !== undefined || f.gid !== undefined ? ` · ${f.uid ?? '?'}:${f.gid ?? '?'}` : ''}
                </td>
                <td>
                  <StatusBadge kind="file" value={f.state === 'missing' && !f.required ? 'missing-optional' : f.state} size="xs" />
                </td>
                <td className="text-xs max-sm:hidden">
                  {f.state === 'set' ? (
                    <>
                      v{f.version} · {formatBytes(f.size)}
                      {f.sha256 ? <div className="font-mono text-base-content/60" title={f.sha256}>sha256 {f.sha256.slice(0, 12)}…</div> : null}
                      <div className="text-base-content/60">
                        <RelativeTime value={f.updatedAt} />
                        {f.updatedBy ? ` by ${f.updatedBy}` : ''}
                      </div>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="whitespace-nowrap text-right">
                  {cfg.can.writeConfig ? (
                    <Haptic disabled={upload.isPending}>
                      <button type="button" className="btn btn-ghost btn-xs" onClick={() => pick(f.name)} disabled={upload.isPending}>
                        {upload.isPending && uploadFor === f.name ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : <Upload className="size-3.5" aria-hidden="true" />}
                        {f.state === 'set' ? 'Replace' : 'Upload'}
                      </button>
                    </Haptic>
                  ) : null}
                  {f.state === 'set' && cfg.can.revealSecrets ? (
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => setDownloading(f)} aria-label={`Download ${f.name}`}>
                      <Download className="size-3.5" aria-hidden="true" />
                    </button>
                  ) : null}
                  {f.state === 'set' && cfg.can.writeConfig ? (
                    <button type="button" className="btn btn-ghost btn-xs dr-ink-error" onClick={() => setDeleting(f)} aria-label={`Delete ${f.name}`}>
                      <Trash2 className="size-3.5" aria-hidden="true" />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {deleting ? (
        <ConfirmDialog
          title={`Delete ${deleting.name} in ${env}?`}
          confirmLabel="Delete file"
          tone="error"
          busy={del.isPending}
          error={del.error}
          onCancel={() => (setDeleting(null), del.reset())}
          onConfirm={() => del.mutate(deleting.name, { onSuccess: () => (toast.success(`${deleting.name} deleted`, { description: `Removed from ${env}.` }), setDeleting(null)), onError: () => haptic('error') })}
        >
          <p>
            The stored content is removed. Running targets keep their current copy until the next deployment or configuration apply
            {deleting.required ? ', which will then be blocked because this file is required' : ''}.
          </p>
        </ConfirmDialog>
      ) : null}
      {downloading ? (
        <ConfirmDialog
          title={`Download ${downloading.name}?`}
          confirmLabel="Download"
          tone="warning"
          busy={reveal.isPending}
          error={reveal.error}
          onCancel={() => (setDownloading(null), reveal.reset())}
          onConfirm={() =>
            reveal.mutate(downloading.name, {
              onSuccess: (r) => {
                save(downloading.name, r.contentBase64);
                setDownloading(null);
              },
              onError: () => haptic('error'),
            })
          }
        >
          <p>The decrypted file is saved to your computer. The download is recorded in the audit log with your name.</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
